import { apiGet, apiPost } from "./genesisApi/httpsMethods";
import type { ApiResponse } from "./genesisApi/httpsMethods";

/** Server-computed position in the Badger -> Hawk flow. */
export type HawkStage =
  | "not_started"
  | "awaiting_deal_lead"
  | "awaiting_asset_manager"
  | "ready_to_send"
  | "in_hawk";

export type HawkUserRef = { id: number; name: string | null };

export type HawkApproval = {
  approved: boolean;
  /** ISO timestamp. */
  at: string | null;
  /** Null for approvals given before approvers were recorded. */
  by: HawkUserRef | null;
};

export type HawkSyncAttempt = {
  id: number;
  /** "assets" only on attempts logged before updates replaced it. */
  scope: "full" | "update" | "assets";
  status: "success" | "failed";
  created_at: string | null;
  http_status: number | null;
  error_message: string | null;
  hawk_loan_id: string | null;
};

export type HawkPermissions = {
  can_request: boolean;
  can_review_deal_lead: boolean;
  can_review_asset_manager: boolean;
  can_send: boolean;
  can_send_update: boolean;
};

export type HawkStatus = {
  deal_id: number;
  deal_name: string | null;
  stage: HawkStage;
  /** The menu item only shows on closed deals (or once the flow has started). */
  deal_closed: boolean;
  /** Why the next step can't happen, worded for the user. */
  blocking_reason: string | null;
  permissions: HawkPermissions;
  deal_lead: { id: number; name: string | null; email: string | null } | null;
  asset_managers: { email: string; name: string | null; id: number | null }[];
  requested: {
    at: string | null;
    by: HawkUserRef | null;
    sent_to: string | null;
  } | null;
  deal_lead_approval: HawkApproval;
  asset_manager_approval: HawkApproval;
  hawk_loan_id: string | null;
  hawk_loan_url: string | null;
  last_sync: HawkSyncAttempt | null;
  updates_enabled: boolean;
};

export type HawkPushResult = { loan_id?: string | number; loan_url?: string };

export type HawkUpdateResult = {
  deal_id: number;
  loan_id: string;
  loan_url: string | null;
  /** False only if Hawk applied the update straight away (an older Hawk). */
  staged: boolean;
};

/** Error text from an unsuccessful response (`detail` may be `{ code, message }`). */
export function hawkErrorMessage(
  response: ApiResponse<unknown>,
  fallback: string,
): string {
  const { message } = response;
  if (typeof message === "string" && message.trim()) return message;
  if (message && typeof message === "object" && message.message) {
    return message.message;
  }
  return fallback;
}

/** GET /hawk-loans/status?deal_id= */
export async function getHawkStatus(dealId: number) {
  return apiGet<HawkStatus>("hawk-loans/status", { deal_id: dealId });
}

/** POST /hawk-loans/request — emails the deal lead. */
export async function requestHawkApproval(dealId: number, resend = false) {
  const params = new URLSearchParams({
    deal_id: String(dealId),
    resend: String(resend),
  });
  return apiPost<{ already_requested: boolean; sent_to: string | null }>(
    `hawk-loans/request?${params.toString()}`,
    {},
  );
}

/** POST /hawk-loans/review/deal-lead — deal lead only. */
export async function setDealLeadReview(dealId: number, approved: boolean) {
  return apiPost(`hawk-loans/review/deal-lead?deal_id=${dealId}`, { approved });
}

/** POST /hawk-loans/review/asset-manager — asset managers only. */
export async function setAssetManagerReview(dealId: number, approved: boolean) {
  return apiPost(`hawk-loans/review/asset-manager?deal_id=${dealId}`, {
    approved,
  });
}

/** POST /hawk-loans/push — needs both approvals; creates the loan in Hawk. */
export async function pushDealToHawk(dealId: number) {
  return apiPost<HawkPushResult>(`hawk-loans/push?deal_id=${dealId}`, {});
}

/**
 * POST /hawk-loans/push-update — re-sends the deal to its Hawk loan, where a
 * Hawk manager reviews it before anything changes.
 */
export async function sendUpdateToHawk(dealId: number) {
  return apiPost<HawkUpdateResult>(
    `hawk-loans/push-update?deal_id=${dealId}`,
    {},
  );
}
